import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import type { Rank } from '@/types';
import type { SkillLevel } from '@/components/SkillLevelBadge';

type Profile = Database['public']['Tables']['profiles']['Row'];
type AppRank = Database['public']['Enums']['app_rank'];
type DbSkillLevel = Database['public']['Enums']['skill_level'];

interface UsePlayersOptions {
  limit?: number;
  enabled?: boolean;
  queryKeyPrefix?: string;
}

export function usePlayers(
  rankFilter?: Rank | 'all',
  searchQuery?: string,
  skillLevelFilter?: SkillLevel | 'all',
  locationFilter?: string | null,
  options?: UsePlayersOptions
) {
  const { limit, enabled = true, queryKeyPrefix = 'players' } = options ?? {};

  return useQuery({
    queryKey: [queryKeyPrefix, rankFilter, searchQuery, skillLevelFilter, locationFilter, limit],
    enabled,
    queryFn: async () => {
      // Use profiles_public view to avoid exposing PII (email, phone)
      let query = supabase
        .from('profiles_public')
        .select('*')
        .order('rank_points', { ascending: false });

      if (rankFilter && rankFilter !== 'all') {
        query = query.eq('rank', rankFilter as AppRank);
      }

      // Search by name or player_id
      if (searchQuery) {
        const trimmedSearch = searchQuery.trim().toUpperCase();
        // If search looks like a player ID (6 alphanumeric chars), search by player_id
        if (/^[A-Z0-9]{1,6}$/.test(trimmedSearch)) {
          query = query.or(`name.ilike.%${searchQuery}%,player_id.ilike.${trimmedSearch}%`);
        } else {
          query = query.ilike('name', `%${searchQuery}%`);
        }
      }

      if (skillLevelFilter && skillLevelFilter !== 'all') {
        query = query.eq('skill_level', skillLevelFilter as DbSkillLevel);
      }

      // Location-based filtering
      // We search in both 'location' and 'city' fields for matches
      if (locationFilter) {
        // Extract city/district keywords from the location name
        const locationParts = locationFilter.split(',').map(part => part.trim());
        
        // Build OR conditions for location matching
        const orConditions = locationParts
          .filter(part => part.length > 2) // Skip short parts
          .map(part => `location.ilike.%${part}%,city.ilike.%${part}%`)
          .join(',');
        
        if (orConditions) {
          query = query.or(orConditions);
        }
      }

      if (limit && limit > 0) {
        query = query.limit(limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Profile[];
    },
  });
}
